import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    createManufacturerWarrantyTemplate,
    deleteManufacturerWarrantyTemplate,
    fetchManufacturerActiveWarranties,
    fetchManufacturerWarrantyCategories,
    fetchManufacturerWarrantyItems,
    fetchManufacturerWarrantyStats,
    fetchManufacturerWarrantyTemplates,
    fetchManufacturerWarrantyTypes,
    updateManufacturerWarrantyTemplate,
} from '../../../../services/Http/ManufacturerApi';
import DataTable from 'react-data-table-component';
import { MdAdd, MdSearch } from 'react-icons/md';
import { SimpleSelect } from '../../../../shared/layout/forms/AppSelect';

const tabs = [
    { key: 'templates', label: 'ManufacturerWarranties.tabs.templates' },
    { key: 'active', label: 'ManufacturerWarranties.tabs.active' },
];

const ManufacturerWarranties = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('templates');
    const [stats, setStats] = useState(null);

    useEffect(() => {
        const loadStats = async () => {
            try {
                const response = await fetchManufacturerWarrantyStats();
                setStats(response);
            } catch (error) {
                toast.error(t('ManufacturerWarranties.errors.loadStats'));
            }
        };

        loadStats();
    }, [t]);

    return (
        <Wrapper>
            <Header>
                <div>
                    <Title>{t('ManufacturerWarranties.title')}</Title>
                    <Subtitle>{t('ManufacturerWarranties.subtitle')}</Subtitle>
                </div>
            </Header>

            <StatsGrid>
                <StatCard>
                    <label>{t('ManufacturerWarranties.stats.templates')}</label>
                    <strong>{stats?.templates ?? '–'}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('ManufacturerWarranties.stats.active')}</label>
                    <strong>{stats?.active_warranties ?? '–'}</strong>
                </StatCard>
                <StatCard>
                    <label>{t('ManufacturerWarranties.stats.expiring')}</label>
                    <strong>{stats?.expiring_soon ?? '–'}</strong>
                </StatCard>
            </StatsGrid>

            <Tabs>
                {tabs.map((tab) => (
                    <TabButton key={tab.key} $active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>
                        {t(tab.label)}
                    </TabButton>
                ))}
            </Tabs>

            {activeTab === 'templates' ? <TemplatesSection /> : <ActiveWarrantiesSection />}
        </Wrapper>
    );
};

const TemplatesSection = () => {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, per_page: 10, total: 0 });
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [types, setTypes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(defaultTemplateForm);

    useEffect(() => {
        const loadLists = async () => {
            try {
                const [typeData, categoryData, itemData] = await Promise.all([
                    fetchManufacturerWarrantyTypes(),
                    fetchManufacturerWarrantyCategories(),
                    fetchManufacturerWarrantyItems({ perPage: 200 }),
                ]);
                setTypes(typeData || []);
                setCategories(categoryData || []);
                setItems(itemData?.data || []);
            } catch (error) {
                toast.error(t('ManufacturerWarranties.errors.loadFormMeta'));
            }
        };

        loadLists();
    }, [t]);

    const loadTemplates = async (page = pagination.current_page, perPage = pagination.per_page) => {
        setLoading(true);
        try {
            const data = await fetchManufacturerWarrantyTemplates({ page, perPage });
            setTemplates(data.data || []);
            setPagination({ current_page: data.current_page, per_page: data.per_page, total: data.total });
        } catch (error) {
            toast.error(t('ManufacturerWarranties.errors.loadTemplates'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    const handleSubmit = async () => {
        const payload = serializeTemplate(form);
        try {
            if (editing) {
                await updateManufacturerWarrantyTemplate(editing.id, payload);
                toast.success(t('ManufacturerWarranties.notifications.updated'));
            } else {
                await createManufacturerWarrantyTemplate(payload);
                toast.success(t('ManufacturerWarranties.notifications.created'));
            }
            setShowModal(false);
            setForm(defaultTemplateForm);
            setEditing(null);
            loadTemplates();
        } catch (error) {
            toast.error(t('ManufacturerWarranties.errors.saveTemplate'));
        }
    };

    const handleDelete = async (template) => {
        if (!window.confirm(t('ManufacturerWarranties.confirmDelete'))) {
            return;
        }

        try {
            await deleteManufacturerWarrantyTemplate(template.id);
            toast.success(t('ManufacturerWarranties.notifications.deleted'));
            loadTemplates();
        } catch (error) {
            toast.error(t('ManufacturerWarranties.errors.deleteTemplate'));
        }
    };

    const columns = [
        { name: t('ManufacturerWarranties.table.duration'), selector: (row) => `${row.duration_months} ${t('ManufacturerWarranties.table.months')}` },
        { name: t('ManufacturerWarranties.table.type'), selector: (row) => row.type?.name || '—' },
        { name: t('ManufacturerWarranties.table.status'), selector: (row) => (row.is_active ? t('common.active') : t('common.inactive')) },
        {
            name: t('ManufacturerWarranties.table.actions'),
            cell: (row) => (
                <ActionGroup>
                    <button onClick={() => { setEditing(row); setForm(deserializeTemplate(row)); setShowModal(true); }}>{t('common.edit')}</button>
                    <button onClick={() => handleDelete(row)}>{t('common.delete')}</button>
                </ActionGroup>
            ),
        },
    ];

    return (
        <section>
            <SectionHeader>
                <SearchForm>
                    <MdSearch />
                    <input placeholder={t('ManufacturerWarranties.table.searchPlaceholder')} disabled />
                </SearchForm>
                <PrimaryButton onClick={() => { setEditing(null); setForm(defaultTemplateForm); setShowModal(true); }}>
                    <MdAdd /> {t('ManufacturerWarranties.actions.newTemplate')}
                </PrimaryButton>
            </SectionHeader>
            <DataTable
                columns={columns}
                data={templates}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={pagination.total}
                paginationPerPage={pagination.per_page}
                onChangePage={(page) => loadTemplates(page, pagination.per_page)}
                onChangeRowsPerPage={(perPage, page) => loadTemplates(page, perPage)}
                highlightOnHover
            />
            {showModal && (
                <TemplateModal
                    form={form}
                    setForm={setForm}
                    types={types}
                    categories={categories}
                    items={items}
                    onClose={() => { setShowModal(false); setEditing(null); }}
                    onSubmit={handleSubmit}
                    isEditing={Boolean(editing)}
                />
            )}
        </section>
    );
};

const ActiveWarrantiesSection = () => {
    const { t } = useTranslation();
    const [records, setRecords] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, per_page: 10, total: 0 });
    const [loading, setLoading] = useState(false);

    const loadActive = async (page = pagination.current_page, perPage = pagination.per_page) => {
        setLoading(true);
        try {
            const response = await fetchManufacturerActiveWarranties({ page, perPage });
            setRecords(response.data || []);
            setPagination({ current_page: response.current_page, per_page: response.per_page, total: response.total });
        } catch (error) {
            toast.error(t('ManufacturerWarranties.errors.loadActive'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadActive();
    }, []);

    const columns = [
        { name: t('ManufacturerWarranties.active.serial'), selector: (row) => row.serial_number || '—' },
        { name: t('ManufacturerWarranties.active.buyer'), selector: (row) => `${row.buyer?.name || ''} ${row.buyer?.surname || ''}`.trim() || '—' },
        { name: t('ManufacturerWarranties.active.activated'), selector: (row) => formatDate(row.start_date) },
        { name: t('ManufacturerWarranties.active.expires'), selector: (row) => formatDate(row.end_date) },
    ];

    return (
        <DataTable
            columns={columns}
            data={records}
            progressPending={loading}
            pagination
            paginationServer
            paginationTotalRows={pagination.total}
            paginationPerPage={pagination.per_page}
            onChangePage={(page) => loadActive(page, pagination.per_page)}
            onChangeRowsPerPage={(perPage, page) => loadActive(page, perPage)}
        />
    );
};

const TemplateModal = ({ form, setForm, types, categories, items, onClose, onSubmit, isEditing }) => {
    const { t } = useTranslation();

    return (
        <ModalOverlay>
            <ModalContent>
                <h3>{isEditing ? t('ManufacturerWarranties.modal.editTitle') : t('ManufacturerWarranties.modal.createTitle')}</h3>
                <FormGrid>
                    <FormField>
                        <label>{t('ManufacturerWarranties.modal.fields.name')}</label>
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </FormField>
                    <FormField>
                        <label>{t('ManufacturerWarranties.modal.fields.duration')}</label>
                        <input type="number" min="1" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} />
                    </FormField>
                    <FormField>
                        <label>{t('ManufacturerWarranties.modal.fields.type')}</label>
                        <SimpleSelect
                            value={form.warranty_template_type_id || ''}
                            onChange={(event, nextValue) => setForm({ ...form, warranty_template_type_id: nextValue ?? event?.target?.value ?? '' })}
                            options={[
                                { value: '', label: t('common.select') },
                                ...types.map((type) => ({ value: type.id, label: type.name })),
                            ]}
                            inputId="warranty-template-type"
                        />
                    </FormField>
                    <FormField>
                        <label>{t('ManufacturerWarranties.modal.fields.category')}</label>
                        <SimpleSelect
                            value={form.category_id || ''}
                            onChange={(event, nextValue) => setForm({ ...form, category_id: nextValue ?? event?.target?.value ?? '' })}
                            options={[
                                { value: '', label: t('common.select') },
                                ...categories.map((cat) => ({ value: cat.id, label: cat.name })),
                            ]}
                            inputId="warranty-template-category"
                        />
                    </FormField>
                    <FormField>
                        <label>{t('ManufacturerWarranties.modal.fields.item')}</label>
                        <SimpleSelect
                            value={form.manufacturer_item_id || ''}
                            onChange={(event, nextValue) => setForm({ ...form, manufacturer_item_id: nextValue ?? event?.target?.value ?? '' })}
                            options={[
                                { value: '', label: t('ManufacturerWarranties.modal.fields.anyItem') },
                                ...items.map((item) => ({ value: item.id, label: item.product_name || item.id })),
                            ]}
                            inputId="warranty-template-item"
                        />
                    </FormField>
                    <FormField $full>
                        <label>{t('ManufacturerWarranties.modal.fields.description')}</label>
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
                    </FormField>
                    <FormField $full>
                        <label>{t('ManufacturerWarranties.modal.fields.terms')}</label>
                        <textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} rows={3} />
                    </FormField>
                    <FormField $full>
                        <label>{t('ManufacturerWarranties.modal.fields.claimProcess')}</label>
                        <textarea value={form.claim_process} onChange={(e) => setForm({ ...form, claim_process: e.target.value })} rows={3} />
                    </FormField>
                    <FormField>
                        <label>{t('ManufacturerWarranties.modal.fields.active')}</label>
                        <ToggleSwitch>
                            <input type="checkbox" id="warrantyActive" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                            <Slider htmlFor="warrantyActive" />
                        </ToggleSwitch>
                    </FormField>
                </FormGrid>
                <ModalActions>
                    <ModalButton type="button" onClick={onClose}>{t('common.cancel')}</ModalButton>
                    <PrimaryButton onClick={onSubmit}>{t('common.save')}</PrimaryButton>
                </ModalActions>
            </ModalContent>
        </ModalOverlay>
    );
};

const defaultTemplateForm = {
    name: '',
    description: '',
    duration_months: 12,
    warranty_template_type_id: '',
    category_id: '',
    manufacturer_item_id: '',
    terms: '',
    claim_process: '',
    is_active: true,
};

const deserializeTemplate = (template) => ({
    name: template.name || '',
    description: template.description || '',
    duration_months: template.duration_months || 12,
    warranty_template_type_id: template.warranty_template_type_id || '',
    category_id: template.category_id || '',
    manufacturer_item_id: template.manufacturer_item_id || '',
    terms: Array.isArray(template.terms) ? template.terms.join('\n') : '',
    claim_process: Array.isArray(template.claim_process) ? template.claim_process.join('\n') : '',
    is_active: Boolean(template.is_active),
});

const serializeTemplate = (form) => ({
    name: form.name,
    description: form.description,
    duration_months: Number(form.duration_months) || 1,
    warranty_template_type_id: form.warranty_template_type_id || null,
    category_id: form.category_id || null,
    manufacturer_item_id: form.manufacturer_item_id || null,
    terms: form.terms ? form.terms.split('\n').map((line) => line.trim()).filter(Boolean) : null,
    claim_process: form.claim_process ? form.claim_process.split('\n').map((line) => line.trim()).filter(Boolean) : null,
    is_active: form.is_active,
});

const ActionGroup = styled.div`
    display: flex;
    gap: 0.5rem;

    button {
        border: 1px solid rgba(130, 22, 146, 0.3);
        background: transparent;
        border-radius: 6px;
        padding: 0.25rem 0.625rem;
        cursor: pointer;
    }
`;

const Wrapper = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Title = styled.h1`
    margin: 0;
    color: rgba(130, 22, 146, 1);
`;

const Subtitle = styled.p`
    margin: 0.25rem 0 0 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 1rem;
    margin: 1.5rem 0;
`;

const StatCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1rem;

    label {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
        display: block;
        margin-bottom: 0.375rem;
    }

    strong {
        font-size: 1.6rem;
        color: rgba(130, 22, 146, 1);
    }
`;

const Tabs = styled.div`
    display: flex;
    border-bottom: 1px solid rgba(130, 22, 146, 0.2);
`;

const TabButton = styled.button`
    background: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 0.08)' : 'transparent')};
    border: none;
    border-bottom: ${({ $active }) => ($active ? '3px solid rgba(130, 22, 146, 1)' : '3px solid transparent')};
    color: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 1)' : '#444')};
    padding: 0.75rem 1.125rem;
    font-weight: 600;
    cursor: pointer;
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    margin: 1.5rem 0;
`;

const SearchForm = styled.div`
    flex: 1;
    min-width: 220px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 8px;
    padding: 0.375rem 0.75rem;

    input {
        border: none;
        flex: 1;
        outline: none;
    }
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 0.625rem 1rem;
    cursor: pointer;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: #fff;
    border-radius: 12px;
    max-width: 600px;
    width: 90%;
    padding: 1.5rem;
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
`;

const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;

    textarea,
    input,
    select {
        width: 100%;
        border: 1px solid rgba(130, 22, 146, 0.2);
        border-radius: 8px;
        padding: 0.625rem;
    }

    ${({ $full }) => $full && `grid-column: span 2;`}
`;

const ToggleSwitch = styled.div`
    position: relative;
    width: 48px;
    height: 26px;

    input {
        opacity: 0;
        width: 0;
        height: 0;
    }
`;

const Slider = styled.label`
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(130, 22, 146, 0.2);
    transition: 0.2s;
    border-radius: 34px;

    &::before {
        position: absolute;
        content: '';
        height: 20px;
        width: 20px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        transition: 0.2s;
        border-radius: 50%;
    }

    input:checked + & {
        background-color: rgba(130, 22, 146, 1);
    }

    input:checked + &::before {
        transform: translateX(22px);
    }
`;

const ModalActions = styled.div`
    margin-top: 1.5rem;
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
`;

const ModalButton = styled.button`
    border-radius: 8px;
    border: 1px solid rgba(130, 22, 146, 0.3);
    background: transparent;
    padding: 0.625rem 1.125rem;
    cursor: pointer;
`;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

export default ManufacturerWarranties;
